/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditFeatureDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-FEATURES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditFeatureDialog - Диалог редактирования задачи с превью и секциями.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - UC-020: поле Статус (В работе / Бэклог).
 * LAST_CHANGE: 2026-07-14 - Вертикальный скролл длинной формы через formDialogContentSx.
 * LAST_CHANGE: 2026-07-14 - UX: шапка бэклога, превью задачи, секции формы, primary CTA.
 * LAST_CHANGE: 2026-07-13 - Anti-autocomplete и mergeAppDataFieldSlotProps для полей формы.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import AppDateField from '../../../components/AppDateField';
import FormDialogHeader, {
  formDialogActionsSx,
  formDialogContentSx,
  formDialogFormSx,
  formDialogPaperSlotProps,
} from '../../../components/FormDialogHeader';
import FormSection from '../../../components/FormSection';
import FeatureEstimateFields, {
  buildEstimatesPayload,
  estimatesToRoleMap,
  isEstimateInputValid,
} from './FeatureEstimateFields';
import { APP_FORM_AUTOCOMPLETE, mergeAppDataFieldSlotProps } from '../../../utils/formFieldConfig';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isNonEmptyTrimmed } from '../../../utils/formValidators';

const ASSIGNABLE_STATUSES = [
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'BACKLOG', label: 'Бэклог' },
];

const STATUS_DISPLAY = {
  BACKLOG: { label: 'Бэклог', color: 'default' },
  IN_PROGRESS: { label: 'В работе', color: 'primary' },
  ARCHIVED: { label: 'В архиве', color: 'default' },
};

function formatPreviewDate(isoDate) {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

function sumEstimates(estimatesByRoleId) {
  return Object.values(estimatesByRoleId).reduce((sum, raw) => {
    if (raw === '' || raw == null) return sum;
    const num = parseFloat(raw);
    return Number.isNaN(num) ? sum : sum + num;
  }, 0);
}

/* START_METHOD_EditFeatureDialog */
/* START_CONTRACT: EditFeatureDialog
 * PURPOSE: Диалог редактирования задачи (название, статус, дата старта, оценки) с предзаполнением.
 * INPUTS: { open: boolean, onClose: ()=>void, onUpdate: (id,payload)=>Promise, feature: Feature, projectRoles: ProjectRole[] }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onUpdate (HTTP наверх); локальный state.
 * LINKS: M-FE-FEATURES, UC-020
 * END_CONTRACT: EditFeatureDialog
 */
export default function EditFeatureDialog({
  open,
  onClose,
  onUpdate,
  feature,
  projectRoles,
}) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const [startDate, setStartDate] = useState('');
  const [status, setStatus] = useState('IN_PROGRESS');
  const [estimatesByRoleId, setEstimatesByRoleId] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (feature) {
      nameField.reset(feature.name);
      setStartDate(feature.startDate ?? '');
      setStatus(
        feature.status === 'BACKLOG' || feature.status === 'IN_PROGRESS'
          ? feature.status
          : 'IN_PROGRESS',
      );
      setEstimatesByRoleId(estimatesToRoleMap(feature.estimates));
    }
  }, [feature]);

  if (!feature) return null;

  const trimmedName = nameField.value.trim();
  const previewName = trimmedName || feature.name;
  const totalHours = sumEstimates(estimatesByRoleId);
  const startDateLabel = formatPreviewDate(startDate);
  const statusMeta = STATUS_DISPLAY[status] ?? STATUS_DISPLAY[feature.status] ?? {
    label: feature.statusLabel,
    color: 'default',
  };

  const estimatesValid = Object.values(estimatesByRoleId).every(isEstimateInputValid);
  const canSave = nameField.isValid && estimatesValid && !loading;

  const handleClose = () => {
    if (loading) return;
    resetFormReady();
    onClose();
  };

  const handleEstimateChange = (roleId, value) => {
    setEstimatesByRoleId((prev) => ({ ...prev, [roleId]: value }));
  };

  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!canSave) {
      nameField.touch();
      return;
    }
    setLoading(true);
    try {
      await onUpdate(feature.id, {
        name: trimmedName,
        startDate: startDate || null,
        status,
        estimates: buildEstimatesPayload(estimatesByRoleId, projectRoles),
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-feature-title"
      slotProps={formDialogPaperSlotProps(dialogTransitionSlotProps)}
    >
      <Box
        component="form"
        autoComplete={APP_FORM_AUTOCOMPLETE}
        onSubmit={handleUpdate}
        noValidate
        sx={formDialogFormSx}
      >
        <FormDialogHeader
          overline="Бэклог"
          title="Редактирование задачи"
          titleId="edit-feature-title"
          subtitle="Изменения пересчитают позицию на таймлайне."
        />

        <DialogContent sx={formDialogContentSx}>
          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2.5,
              borderColor: alpha('#2563a8', 0.2),
              bgcolor: feature.isArchived ? 'background.default' : alpha('#2563a8', 0.04),
              opacity: feature.isArchived ? 0.85 : 1,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 0.5,
                bgcolor: alpha('#2563a8', 0.1),
                color: 'primary.main',
                fontFamily: '"Source Sans 3", monospace',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            >
              {(feature.sortOrder ?? 0) + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                noWrap
                sx={{
                  textDecoration: feature.isArchived ? 'line-through' : 'none',
                  color: feature.isArchived ? 'text.secondary' : 'text.primary',
                }}
              >
                {previewName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                {startDateLabel && (
                  <Typography variant="caption" color="text.secondary">
                    Старт {startDateLabel}
                  </Typography>
                )}
                {totalHours > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: '"Source Sans 3", monospace', fontWeight: 600, color: 'text.secondary' }}
                  >
                    {totalHours} ч
                  </Typography>
                )}
              </Box>
            </Box>
            <Chip
              label={statusMeta.label}
              size="small"
              color={statusMeta.color}
              variant={feature.isArchived ? 'outlined' : 'filled'}
              sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
            />
          </Paper>

          <FormSection label="Основное" first>
            <TextField
              inputRef={firstFieldRef}
              required
              fullWidth
              label="Название"
              name="teamplanner-feature-name"
              value={nameField.value}
              onChange={nameField.onChange}
              onBlur={nameField.onBlur}
              size="small"
              disabled={loading}
              error={nameField.showError}
              helperText={nameField.showError ? 'Введите название задачи' : ' '}
              slotProps={mergeAppDataFieldSlotProps({
                htmlInput: { maxLength: 200 },
              })}
            />
            <TextField
              select
              fullWidth
              label="Статус"
              name="teamplanner-feature-status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              size="small"
              disabled={loading}
            >
              {ASSIGNABLE_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <AppDateField
              label="Дата старта"
              name="teamplanner-feature-start-date"
              value={startDate}
              onChange={setStartDate}
              disabled={loading}
              clearable
            />
          </FormSection>

          <FormSection label="Оценки по ролям">
            <FeatureEstimateFields
              roles={projectRoles}
              estimatesByRoleId={estimatesByRoleId}
              onEstimateChange={handleEstimateChange}
              disabled={loading}
            />
          </FormSection>
        </DialogContent>

        <DialogActions sx={formDialogActionsSx}>
          <Button type="button" size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
/* END_METHOD_EditFeatureDialog */
